import { ColumnDef } from "@tanstack/react-table";
import { cn } from "@theprelude/core";
import {
  ChevronIcon,
  IconButton,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  ScrollCorner,
} from "@theprelude/ds";
import {
  getColumnVisibility,
  protectedColumn,
} from "@/components/table/columns";
import { GridTable } from "@/components/table/grid-table";
import { useTable } from "@/components/table/hooks";

type QuickViewTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  isLoading: boolean;
  columnVisibility?: Record<string, boolean>;
  name: string;
};

export default function QuickViewTable<T>({
  data,
  columns,
  isLoading,
  name,
}: QuickViewTableProps<T>) {
  const table = useTable({
    name,
    data,
    columns,
    initialState: {
      pagination: { pageSize: 5 },
      sorting: [{ id: protectedColumn.id, desc: false }],
      columnVisibility: getColumnVisibility(columns),
    },
  });

  const getPaginationText = () => {
    const firstIndex = table.getState().pagination.pageIndex * 5 + 1;
    const secondIndex = table.getState().pagination.pageIndex * 5 + 5;
    const lastIndex = Object.values(table.getRowModel().rowsById).length;

    if (secondIndex > lastIndex) {
      return `${firstIndex}-${lastIndex} of ${lastIndex}`;
    }
    return `${firstIndex}-${secondIndex} of ${lastIndex}`;
  };

  const rowCount = Object.values(table.getRowModel().rowsById).length;
  const showPagination = rowCount > 5;

  return (
    <ScrollAreaRoot>
      <ScrollAreaViewport>
        <GridTable
          table={table}
          className={cn({
            "h-full":
              table.getRowModel().rows.length >= 5 ||
              table.getRowModel().rows.length === 0,
          })}
          loading={isLoading}
          emptyState={
            <span className="flex justify-center text-center text-secondary-foreground text-sm">
              There are no results to display at this time. Please add a probe,
              schedule more tests or check back later.
            </span>
          }
        >
          {showPagination && (
            <div className="flex items-center justify-start gap-2 border-secondary-background border-t px-2 py-2 md:justify-end">
              <span className="pr-2 text-xs">{getPaginationText()}</span>
              <IconButton
                icon={<ChevronIcon className="rotate-[270deg]" />}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                icon={<ChevronIcon className="rotate-[90deg]" />}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
            </div>
          )}
        </GridTable>
      </ScrollAreaViewport>
      <ScrollBar orientation="horizontal" className="z-10" />
      <ScrollCorner />
    </ScrollAreaRoot>
  );
}
